import { useState } from 'react';
import { IndicePenetrazioneCash } from '../../../../api/apiTypes';
import { Link } from 'react-router-dom';
import { routesMap } from '../../../../routes/AppRoutes';
import { validaIndiceDiPentrazione, } from '../../../../api/functionApi';
import Swal from 'sweetalert2';
import { ConfirmValidationDialog } from '../../../../utils/dialog/ConfirmValidationDialog';
import ReportStatusBadge from '../../../../utils/ReportStatusBadge';
import { useAxiosInterceptor } from '../../../../api/wrappers';
import { Eye } from 'lucide-react';

type ContainerListaDriverPageProps = {
  indici: IndicePenetrazioneCash[];
  driverRoute: string | undefined;
  reportId: string;
  id: string;
  driverId: string;
  indiceID: string;
};

const ContainerIndiceDiPenetrazionePage = ({ reportId, indici, driverRoute, id, indiceID, driverId }: ContainerListaDriverPageProps) => {
  useAxiosInterceptor();
  const [showDialogConfermaValidazione, setShowDialogConfermaValidazione] = useState(false);
  const [validated, setValidated] = useState(indici[0]?.validation === "validated");

  const handleValidazione = async () => {
    try {
      await validaIndiceDiPentrazione(id, driverId, indiceID);
      setValidated(true);
      Swal.fire({
        icon: 'success',
        title: 'Validazione completata!',
        text: 'La strategia è stata validata correttamente.',
        confirmButtonColor: '#374151',
      });
    } catch (error) {
      console.error('Errore durante la validazione:', error);
      Swal.fire({
        icon: 'error',
        title: 'Errore nella validazione',
        text: 'Si è verificato un errore durante la validazione.',
        confirmButtonColor: '#374151',
      });
    } finally {
      setShowDialogConfermaValidazione(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* TABELLA 1 - Valori per Mercato */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-semibold text-[#1B365D]">Valori scambiati per Mercato</h2>
          <ReportStatusBadge stato={validated ? "validated" : indici[0]?.validation} />
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-4 md:grid-cols-12 gap-x-4 px-6 py-3.5 border-b border-gray-100 bg-gray-50 font-semibold text-[#1B365D] text-xs">
            <div className="col-span-1 md:col-span-3 flex items-center">Mercato</div>
            <div className="col-span-1 md:col-span-3 flex justify-center items-center">Valore Scambiato</div>
            <div className="col-span-1 md:col-span-3 flex justify-center items-center">% sul Totale</div>
            <div className="col-span-1 md:col-span-3" />
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-100">
            {indici.flatMap(indice => indice.traded_values.by_market).map((market, index) => (
              <div
                key={`market-${market.name}-${index}`}
                className="grid grid-cols-4 md:grid-cols-12 gap-x-4 px-6 py-4 hover:bg-gray-50/50 transition duration-150 ease-in-out items-center text-xs"
              >
                <div className="col-span-1 md:col-span-3 text-gray-800 font-medium">{market.name}</div>
                <div className="col-span-1 md:col-span-3 flex justify-center items-center text-gray-800 font-medium">
                  {market.value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                </div>
                <div className="col-span-1 md:col-span-3 flex justify-center items-center text-gray-800 font-medium">
                  {market.percentage_of_total.toFixed(2)}%
                </div>
                <div className="col-span-1 md:col-span-3 flex justify-end">
                  <Link
                    to={routesMap.VISUALIZZA_INTERMEDIARIO(reportId, driverRoute ?? '', market.name)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#1B365D] text-white hover:bg-[#142a4a] transition-colors"
                    state={{ id, driverId, indiceID, validated }}
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {!['approved', 'drafted', 'completed'].includes(indici[0]?.report_status ?? '') && (
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowDialogConfermaValidazione(true)}
              disabled={validated}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                validated
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-[#1B365D] text-white hover:bg-[#142a4a]'
              }`}
            >
              Valida
            </button>
          </div>
        )}
      </div>

      {showDialogConfermaValidazione && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded shadow-lg max-w-sm w-full">
            <ConfirmValidationDialog
              onConferma={handleValidazione}
              onAnnulla={() => setShowDialogConfermaValidazione(false)}
            />
          </div>
        </div>
      )}

      {/* TABELLA 2 - Valori per Banca */}
      <div>
        <h2 className="text-base font-semibold text-[#1B365D] mb-4">Valori scambiati per Banca</h2>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-3 md:grid-cols-12 gap-x-4 px-6 py-3.5 border-b border-gray-100 bg-gray-50 font-semibold text-[#1B365D] text-xs">
            <div className="col-span-1 md:col-span-3 flex items-center">Banca</div>
            <div className="col-span-1 md:col-span-3 flex justify-center items-center">Valore Scambiato</div>
            <div className="col-span-1 md:col-span-3 flex justify-center items-center">% sul Totale</div>
            <div className="col-span-1 md:col-span-3 flex justify-center items-center">Score</div>
          </div>
          <div className="divide-y divide-gray-100">
            {indici.flatMap(indice => indice.traded_values.by_bank).map((bank, index) => (
              <div
                key={`bank-${bank.name}-${index}`}
                className="grid grid-cols-3 md:grid-cols-12 gap-x-4 px-6 py-4 hover:bg-gray-50/50 transition duration-150 ease-in-out items-center text-xs"
              >
                <div className="col-span-1 md:col-span-3 text-gray-800 font-medium">{bank.name}</div>
                <div className="col-span-1 md:col-span-3 flex justify-center items-center text-gray-800 font-medium">
                  {bank.value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                </div>
                <div className="col-span-1 md:col-span-3 flex justify-center items-center text-gray-800 font-medium">
                  {bank.percentage_of_total.toFixed(2)}%
                </div>
                <div className="col-span-1 md:col-span-3 flex justify-center items-center text-gray-800 font-medium">
                  {bank.score.total_score}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContainerIndiceDiPenetrazionePage;
